/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * ChangHeon Yoo (chyoo@rsupport.com)
 *
 * @description
 *
 * @created at Fri Jan 05 2024
 **/

import { useEffect, useMemo, useRef, useState } from "react";

import {
	Button,
	Column,
	Divider,
	Icon,
	Row,
	TextField,
	Tooltip,
	Typography,
	useModal
} from "@/frameworks/designStuff/components";
import { Configure } from "@/frameworks/libraries/utilities";
import { _P, _T } from "@rfice/language";
import { Maybe } from "@/frameworks/libraries/components";
import { UserItem } from "@/frameworks/core/components";
import { useByteCheck } from "@/frameworks/libraries/hooks";

import {
	useChatRoomActions,
	useSetSpaceChatRoomList,
	useSpaceChannelState,
	useSpaceRoomState
} from "@/frameworks/core/chat";
import { ModalID } from "@/frameworks/core/commons";
import { useSpace } from "@/frameworks/core/API";
import * as S from "../emotion";
import { UserChip } from "@/frameworks/core/components";
import { timer } from "rxjs";

interface InviteChatMemberProps extends React.HTMLAttributes<HTMLDivElement> {
	channelID?: string;
	spaceID?: string;
	chatKind: ChatServices.RoomType;
	defaultUsersToInvite?: ValidateUserEmail[];
	onHide: () => void;
}

type ValidateUserEmail = {
	valid: boolean;
	name: string;
	userID: string;
	email: string;
	profileImagePath?: string;
};

export default function InviteSpaceChatMember(props: InviteChatMemberProps) {
	const [spaceUserList, setSpaceUserList] = useState<WebServices.SpaceUser.List.Data>([]);
	const [inviteList, setInviteList] = useState<ValidateUserEmail[]>(props.defaultUsersToInvite ?? []); // submit하는 최종 초대 리스트
	const [searchEmailValue, setSearchEmailValue] = useState(""); // 검색어
	const [searchResult, setSearchResult] = useState<WebServices.Chat.GetRoom.Data["users"]>([]); // 검색어 결과 리스트
	const setSpaceChatRoomList = useSetSpaceChatRoomList();

	const inviteUserEmailsRef = useRef<HTMLInputElement>(null);
	const modal = useModal();

	const spaceAPI = useSpace({
		onSuccess(name) {
			switch (name) {
				case "spaceUser:list": {
					const { data } = spaceAPI.user.list.result;
					if (data) {
						setSpaceUserList(data);
					}
					break;
				}
			}
		}
	});

	const channelInfo = useSpaceChannelState(props.spaceID ?? "");
	const spaceRoom = useSpaceRoomState({ spaceID: props.spaceID ?? "", roomID: props.channelID ?? "" });
	const spaceUserIDList = spaceRoom?.users.map((v) => v.id) ?? [];

	const chatRoomActions = useChatRoomActions();
	const userIDList = inviteList.map((e) => e.userID);
	const inviteUserList = spaceUserList.filter((obj) => userIDList.includes(obj.id));

	const onSubmit = async () => {
		chatRoomActions.spaceChat.addUsers({
			userIds: inviteUserList.map((e) => e.id),
			spaceId: props.spaceID!,
			roomId: props.channelID!,
			events: {
				onSuccess: () => modal.hide(ModalID.INVITE_CHAT_MEMBER),
				onError: () => modal.hide(ModalID.INVITE_CHAT_MEMBER)
			}
		});
	};

	const handleClick = (e: React.MouseEvent<HTMLElement>) => {
		e.stopPropagation();
		const type = e.currentTarget.dataset["type"];

		switch (type) {
			case "confirm":
				onSubmit();
				setSpaceChatRoomList((prev) => {
					if (!prev || !props.channelID) return new Map(prev);

					const newSpaceChatRoomData = new Map(prev);

					if (!props.spaceID) return newSpaceChatRoomData;
					const space = newSpaceChatRoomData.get(props.spaceID);

					if (!space) return newSpaceChatRoomData;

					const targetRoom = space.rooms.find((room) => room.roomId === props.channelID);

					if (targetRoom) {
						const updatedRoom = {
							...targetRoom,
							users: [...targetRoom.users, ...inviteUserList]
						};

						const updatedRooms = space.rooms.map((room) =>
							room.roomId === props.channelID ? updatedRoom : room
						);

						newSpaceChatRoomData.set(props.spaceID, { ...space, rooms: updatedRooms });
					}

					return newSpaceChatRoomData;
				});
		}
		props.onHide();
	};

	const handleChangeField = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { value } = e.target;
		setSearchEmailValue(value);
	};

	const handleKeyDown = (evt: React.KeyboardEvent<HTMLElement>) => {
		const target = evt.target as HTMLInputElement;

		//  검색목록에 유효한 이메일이 있다면, 첫번째 항목을 추가합니다.
		if (evt.key === "Enter" && !evt.shiftKey && evt.nativeEvent.isComposing === false) {
			// 1글자 이상 입력되어야 검색이 되도록 합니다.
			if (target.value.length < 1) return;
			//searchResult의 0번쨰를 inviteList에 넣어주면 됨 중복 판별해서
			const addUser = searchResult[0];
			if (addUser) {
				if (inviteList.map((e) => e.userID).includes(addUser.id)) {
					// 이미 초대된 사람일 때
					setInviteList(inviteList.filter((e) => e.name !== addUser.name));
				} else {
					if (addUser.id === Configure.userID) {
						// 나 자신 초대 경우 예외처리
						return;
					}
					setInviteList((prev) => [
						...prev,
						{ name: addUser.name, email: addUser.username!, userID: addUser.id, valid: true }
					]);
				}
			} else {
				setInviteList((prev) => [...prev, { name: target.value, email: "", userID: "", valid: false }]);
			}
			timer(0).subscribe(() => {
				target.value = "";
				setSearchEmailValue("");
			});
		}

		if (evt.key === "Backspace") {
			if (target.value.length > 0) return;
			const newArr = [...inviteList];
			newArr.pop();
			setInviteList(newArr);
		}
	};

	const filterByEmailAndName = (userList: WebServices.Chat.GetRoom.Data["users"], value: string) => {
		if (value === "") return userList;
		return userList.filter((user) => user.name.includes(value) || user.username?.includes(value));
	};

	const handleUserClick = (user: WebServices.Chat.GetRoom.Data["users"][number]) => {
		if (inviteList.map((e) => e.userID).includes(user.id)) {
			// 이미 초대된 사람인 경우
			setInviteList(inviteList.filter((e) => e.name !== user.name));
		} else {
			setInviteList((prev) => [
				...prev,
				{ name: user.name, email: user.username!, userID: user.id, valid: true }
			]);
		}
		if (inviteUserEmailsRef.current) {
			inviteUserEmailsRef.current.value = "";
		}
	};

	useEffect(() => {
		if (inviteUserEmailsRef.current) {
			inviteUserEmailsRef.current?.focus();
		}
	}, []);

	useEffect(() => {
		spaceAPI.user.list.execute({ spaceId: props.spaceID });
	}, []);

	useEffect(() => {
		const result = filterByEmailAndName(spaceUserList, searchEmailValue);
		setSearchResult(result);
	}, [searchEmailValue]);

	const channelName = useMemo(() => {
		const result = channelInfo?.rooms.filter((room) => room.roomId === props.channelID)[0].roomName;
		return result || "";
	}, [channelInfo]);

	const { isOverLimit } = useByteCheck(50, channelName);

	return (
		<S.Wrapper horizonAlign="left">
			<S.Header horizonAlign="distribute" verticalAlign="top">
				<Column>
					<Maybe
						test={channelInfo === undefined}
						fallback={
							<Typography typoSize="Head6">
								{_P("{{channelName}}에 멤버 추가", {
									channelName: isOverLimit ? channelName.slice(0, 25).concat("...") : channelName
								})}
							</Typography>
						}
					>
						<Typography typoSize="Head6">{channelName}</Typography>
					</Maybe>
					<Typography typoSize="Body4">
						{_T("채널멤버 {{count}}명", {
							count: inviteList.length + spaceUserIDList.length
						})}
					</Typography>
				</Column>
				<S.HeaderIconList>
					<Button.Icon name="Close24" variant="text" size="large" onClick={() => props.onHide()} />
				</S.HeaderIconList>
			</S.Header>
			<S.Body horizonAlign="center">
				<Divider type="line" />
				<S.InnerBody>
					<S.InnerContent>
						<TextField.Tag
							help={_T("이름과 이메일로 검색해주세요.")}
							onChange={handleChangeField}
							onKeyDown={handleKeyDown}
							minHeight="40px"
							ref={inviteUserEmailsRef}
							tags={(() => {
								return inviteList.map((chipData) => (
									<Maybe test={chipData.valid} key={`${chipData.name}`}>
										<UserChip
											userID={chipData.userID || chipData.name}
											key={chipData.userID}
											isValid={true}
											handleClose={() => {
												setInviteList(inviteList.filter((e) => e.name !== chipData.name));
											}}
										/>
										<Maybe.Fallback>
											<Tooltip
												message={
													chipData.email.length === 0
														? _T("다시 확인해주세요.")
														: _T("DM 권한이 없는 사용자 입니다.")
												}
												hasIcon={true}
											>
												<UserChip
													userID={chipData.userID || chipData.name}
													key={chipData.userID}
													isValid={false}
													handleClose={() => {
														setInviteList(
															inviteList.filter((e) => e.name !== chipData.name)
														);
													}}
												/>
											</Tooltip>
										</Maybe.Fallback>
									</Maybe>
								));
							})()}
						/>
					</S.InnerContent>
					{filterByEmailAndName(spaceUserList!, searchEmailValue).length === 0 ||
					(spaceUserList.length === 1 && spaceUserList[0].id === Configure.userID) ? (
						<Row style={{ width: "100%", minHeight: "316px" }} horizonAlign="center" verticalAlign="center">
							<Typography typoSize="Body3_MD">{_T("초대할 멤버가 없습니다.")}</Typography>
						</Row>
					) : (
						<S.UserList>
							{filterByEmailAndName(spaceUserList!, searchEmailValue).map((user, index) => {
								return (
									<S.LI key={user.id}>
										<button
											className="user-button"
											data-email={user.username}
											css={{ width: "100%" }}
											onClick={() => handleUserClick(user)}
											disabled={spaceUserIDList?.includes(user.id)}
										>
											<UserItem key={index} id={user.id}>
												<Maybe
													test={inviteList.map((e) => e.email).includes(user.username || "")}
												>
													<Row
														css={{
															width: "100%"
														}}
														horizonAlign="right"
													>
														<S.CheckIconWrap>
															<Icon name="Check24" size="24" />
														</S.CheckIconWrap>
													</Row>
												</Maybe>
												<Maybe test={spaceUserIDList?.includes(user.id)}>
													<S.MemberTypo typoSize="Button2">{_T("· 멤버")}</S.MemberTypo>
												</Maybe>
											</UserItem>
										</button>
									</S.LI>
								);
							})}
						</S.UserList>
					)}
				</S.InnerBody>
			</S.Body>
			<S.Footer horizonAlign="right" verticalAlign="center">
				<Maybe
					test={inviteList.length === 0 || spaceUserIDList.length === spaceUserList.length}
					fallback={
						<Button.Group>
							<Button filled="secondary" onClick={handleClick} data-type="cancel">
								{_T("취소")}
							</Button>
							<Button
								onClick={handleClick}
								data-type="confirm"
								disabled={inviteList.filter((chip) => !chip.valid).length > 0}
							>
								{_T("적용하기")}
							</Button>
						</Button.Group>
					}
				>
					<Button onClick={handleClick} data-type="cancel">
						{_T("확인")}
					</Button>
				</Maybe>
			</S.Footer>
		</S.Wrapper>
	);
}
